import * as React from 'react';
import { IgcRatingComponent as Component } from 'igniteui-webcomponents';
import type { IgcRatingComponentEventMap } from 'igniteui-webcomponents';
import { type EventName, createComponent } from '../react-props.js';

/**
 * A rating component that allows users to view and provide ratings using customizable symbols.
 * It supports fractional values, hover previews, keyboard navigation, single-selection mode,
 * and integrates with forms as a number input.
 * @slot symbol - Slot for projecting custom `igc-rating-symbol` elements. When used, the number of symbols determines the `max` value.
 * @slot value-label - Slot for custom content displayed alongside the rating value.
 * @fires igcChange - Emitted when the value of the control changes.
 * @fires igcHover - Emitted when hover is enabled and the user mouses over a symbol of the rating.
 * @csspart base - The main wrapper which holds all of the rating elements.
 * @csspart label - The label part.
 * @csspart value-label - The value label part.
 * @csspart symbols - A wrapper for all rating symbols.
 * @csspart symbol - The part of the encapsulated default symbol.
 * @csspart full - The part of the encapsulated full symbols.
 * @csspart empty - The part of the encapsulated empty symbols.
 * @cssproperty --symbol-size - The size of the symbols.
 * @cssproperty --symbol-full-color - The color of the filled symbol.
 * @cssproperty --symbol-empty-color - The color of the empty symbol.
 * @cssproperty --symbol-full-filter - The filter(s) used for the filled symbol.
 * @cssproperty --symbol-empty-filter - The filter(s) used for the empty symbol.
 * @class
 */
export const IgrRating = /* @__PURE__ */ createComponent({
  tagName: 'igc-rating',
  displayName: 'IgrRating',
  elementClass: Component,
  react: React,
  events: {
    onChange: 'igcChange' as EventName<IgcRatingComponentEventMap['igcChange']>,
    onHover: 'igcHover' as EventName<IgcRatingComponentEventMap['igcHover']>,
  },
});

export type IgrRating = Component;

/** @deprecated Module register is no longer needed and can be removed */
export const IgrRatingModule = Component;
